import { requestResourceAccess } from '@api/user.js';
import { ChevronIcon } from '@icons/index.js';
import BasePage from '@pages/BasePage.js';
import { route, setPreviousRoute } from '@router/index.js';
import UserState from '@states/User.js';
import { UIButton, UIDiv, UIForm, UIImage, UIText, UITextArea } from '@ui';
import { logout } from '@utils/logoutHelper.js';

export default class AccessRequest extends BasePage {
  constructor(resourceType = 'file', resourceId = '') {
    super();
    this.setId('access-request');
    this.resourceType = resourceType;
    this.resourceId = resourceId;
    this.userEmail = UserState?.user?.email;
    this.#init();
  }

  #init() {
    const container = new UIDiv();
    container.setClass(
      'flex flex-col items-center justify-center min-h-screen px-4 py-8 bg-white',
    );

    const content = new UIDiv();
    content.setClass('w-full max-w-md');

    // Header with logo
    const header = new UIDiv();
    header.setClass('mb-8 flex flex-col text-left');

    const logo = new UIImage('/static/images/logos/text_blue.svg');
    logo.setClass('h-16 w-auto mx-auto mb-4');

    const title = new UIText(i18next.t('accessRequest.title'));
    title.setClass('text-xl sm:text-2xl font-normal text-gray-800 mb-2');

    const subtitle = new UIText(i18next.t('accessRequest.subtitle'));
    subtitle.setClass('text-sm sm:text-base text-gray-600');

    header.add(logo, title, subtitle);

    // Main content based on user state
    const mainContent = this.userEmail
      ? this.#createRequestForm()
      : this.#createLoginPrompt();

    // User info (only if logged in)
    const userInfo = this.userEmail ? this.#createUserInfo() : null;

    content.add(header, mainContent);
    if (userInfo) content.add(userInfo);

    container.add(content);
    this.add(container);
  }

  #createRequestForm() {
    const form = new UIForm();
    form.setClass('mb-6');

    const messageInput = new UITextArea();
    messageInput.setClass(
      'w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 mb-4 resize-none',
    );
    messageInput.setPlaceholder(i18next.t('accessRequest.messagePlaceholder'));
    messageInput.setRows(4);

    const submitButton = new UIButton(i18next.t('accessRequest.requestBtn'));
    submitButton.setClass(
      'w-full bg-[var(--o-hive-primary-100)] hover:bg-[var(--o-hive-primary-100)] text-white rounded-md py-2 px-4 text-sm font-medium',
    );

    const statusMessage = new UIDiv();
    statusMessage.setClass('mt-4 p-3 rounded hidden');

    // Handle form submission
    submitButton.dom.addEventListener('click', async e => {
      e.preventDefault();

      try {
        submitButton.dom.disabled = true;
        submitButton.dom.textContent = i18next.t('accessRequest.sending');

        await requestResourceAccess({
          resourceType: this.resourceType,
          resourceId: this.resourceId,
          email: this.userEmail,
          message: messageInput.getValue(),
        });

        this.#showStatus(
          statusMessage,
          i18next.t('accessRequest.success'),
          'success',
        );
        messageInput.setValue('');
      } catch (error) {
        console.error('Error sending access request:', error);
        this.#showStatus(
          statusMessage,
          i18next.t('accessRequest.error'),
          'error',
        );
      } finally {
        submitButton.dom.disabled = false;
        submitButton.dom.textContent = i18next.t('accessRequest.requestBtn');
      }
    });

    form.add(messageInput, submitButton, statusMessage);
    return form;
  }

  #createLoginPrompt() {
    const loginButton = new UIButton(i18next.t('accessRequest.loginBtn'));
    loginButton.setClass(
      'w-full bg-[var(--o-hive-primary-100)] hover:bg-[var(--o-hive-primary-100)] text-white rounded-md py-3 px-4 text-sm font-medium mb-6',
    );

    loginButton.onClick(() => {
      setPreviousRoute(window.location.pathname + window.location.search);
      return route('/login');
    });

    return loginButton;
  }

  #createUserInfo() {
    const container = new UIDiv();
    container.setClass(
      'flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-gray-600 whitespace-nowrap pt-4 border-t text-center sm:text-left',
    );

    const emailLine = new UIDiv();
    emailLine.setClass('flex items-center justify-center');

    const emailHelperText = new UIText(
      i18next.t('accessRequest.signedInAs') + ' ',
    );
    emailHelperText.setClass('text-gray-600 mr-2');

    const emailText = new UIText(`${this.userEmail}`);
    emailText.setClass('text-blue-600');

    emailLine.add(emailHelperText, emailText);

    const switchAccount = new UIButton(
      i18next.t('accessRequest.switchAccount'),
    );
    switchAccount.setClass(
      'py-3 px-4 text-sm font-semibold flex items-center justify-center gap-1',
    );

    const chevronIcon = new ChevronIcon({
      size: 16,
      color: 'none',
    });

    switchAccount.add(chevronIcon);
    switchAccount.onClick(() => {
      setPreviousRoute(window.location.pathname + window.location.search);
      logout();
    });

    container.add(emailLine, switchAccount);
    return container;
  }

  #showStatus(statusElement, message, type) {
    statusElement.clear();
    statusElement.removeClass(
      'hidden bg-green-100 text-green-800 bg-red-100 text-red-800',
    );
    statusElement.addClass(
      type === 'success'
        ? 'bg-green-100 text-green-800'
        : 'bg-red-100 text-red-800',
    );

    const text = new UIText(message);
    text.setClass('text-sm');
    statusElement.add(text);
    statusElement.removeClass('hidden');
  }
}
